import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import employeeService from '../../../services/employeeService';
import clientSiteService from '../../../services/clientSiteService';
import NotificationToast from '../../../components/ui/NotificationToast';

const ConvertToEmployeeModal = ({ isOpen, onClose, applicants, clients, onComplete }) => {
  const [selectedClientId, setSelectedClientId] = useState('');
  const [clientSites, setClientSites] = useState([]);
  const [selectedSiteId, setSelectedSiteId] = useState('');
  const [loading, setLoading] = useState(false);
  const [notification, setNotification] = useState(null);
  const [convertData, setConvertData] = useState({});

  React.useEffect(() => {
    if (selectedClientId) {
      loadClientSites(selectedClientId);
    } else {
      setClientSites([]);
      setSelectedSiteId('');
    }
  }, [selectedClientId]);

  const loadClientSites = async (clientId) => {
    try {
      const result = await clientSiteService.getClientSites({ 
        client_id: clientId,
        is_active: 'true' 
      });
      if (result?.success) {
        setClientSites(result.data || []);
      }
    } catch (error) {
      console.error('Error loading client sites:', error);
      setClientSites([]);
    }
  };

  if (!isOpen) return null;

  const showNotification = (message, type = 'success') => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 5000);
  };

  const handleConvert = async () => {
    if (!selectedClientId) {
      showNotification('Please select a client', 'error');
      return;
    }

    if (!selectedSiteId) {
      showNotification('Please select a client site', 'error');
      return;
    }

    if (applicants.length === 0) {
      showNotification('No applicants selected', 'error');
      return;
    }

    try {
      setLoading(true);
      setNotification(null);

      const convertedEmployees = [];
      const errors = [];

      // Convert each applicant to employee
      for (const applicant of applicants) {
        try {
          // Generate employee ID
          const employeeId = `EMP${Date.now()}${Math.random().toString(36).substr(2, 4).toUpperCase()}`;
          
          // Prepare employee data from applicant
          const employeeData = {
            employee_id: employeeId,
            full_name: applicant.applicant_name || applicant.name || `${applicant.first_name || ''} ${applicant.last_name || ''}`.trim(),
            email: applicant.email,
            phone: applicant.phone || '',
            department: applicant.department || '',
            position: applicant.position || '',
            hire_date: new Date().toISOString().split('T')[0],
            status: 'active',
            client_id: selectedClientId,
            client_site_id: selectedSiteId
          };

          const result = await employeeService.createEmployee(employeeData);
          
          if (result.success) {
            convertedEmployees.push(result.data);
          } else {
            errors.push({ applicant: applicant.applicant_name || applicant.name, error: result.error });
          }
        } catch (error) {
          console.error(`Error converting applicant ${applicant.id}:`, error);
          errors.push({ 
            applicant: applicant.applicant_name || applicant.name, 
            error: error.message || 'Conversion failed' 
          });
        }
      }

      if (convertedEmployees.length > 0) {
        showNotification(
          `Successfully converted ${convertedEmployees.length} applicant(s) to employee(s)${errors.length > 0 ? ` (${errors.length} failed)` : ''}`,
          errors.length > 0 ? 'warning' : 'success'
        );
        if (onComplete) {
          setTimeout(() => {
            onComplete();
          }, 1000);
        }
      } else {
        showNotification('Failed to convert applicants. Please try again.', 'error');
      }
    } catch (error) {
      console.error('Error converting applicants:', error);
      showNotification('An error occurred while converting applicants', 'error');
    } finally {
      setLoading(false);
    }
  };

  const selectedClient = clients.find(c => c.id === selectedClientId);
  const selectedSite = clientSites.find(s => s.id === selectedSiteId);

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-card rounded-lg border border-border w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <NotificationToast 
          message={notification?.message} 
          type={notification?.type} 
          onDismiss={() => setNotification(null)} 
        />

        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border sticky top-0 bg-card z-10">
          <div>
            <h2 className="text-xl font-semibold text-foreground">Convert Applicants to Employees</h2>
            <p className="text-sm text-muted-foreground mt-1">
              Convert {applicants.length} applicant(s) to client-wise employees
            </p>
          </div>
          <Button variant="ghost" size="icon" onClick={onClose}>
            <Icon name="X" size={20} />
          </Button>
        </div>

        {/* Form */}
        <div className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Client <span className="text-red-500">*</span>
            </label>
            <Select
              value={selectedClientId}
              onChange={(value) => setSelectedClientId(value)}
              options={[
                { value: '', label: 'Select Client' },
                ...clients.map(client => ({
                  value: client.id,
                  label: client.company_name || client.name
                }))
              ]}
              placeholder="Select Client"
            />
          </div>

          {selectedClientId && (
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Client Site <span className="text-red-500">*</span>
              </label>
              <Select
                value={selectedSiteId}
                onChange={(value) => setSelectedSiteId(value)}
                options={[
                  { value: '', label: 'Select Client Site' },
                  ...clientSites.map(site => ({
                    value: site.id,
                    label: `${site.site_name}${site.location ? ` - ${site.location}` : ''}`
                  }))
                ]}
                placeholder="Select Client Site"
              />
            </div>
          )}

          {(selectedClient || selectedSite) && (
            <div className="bg-muted rounded-lg p-4">
              <p className="text-sm font-medium text-foreground mb-2">Selected Details:</p>
              <div className="space-y-1 text-sm text-muted-foreground">
                {selectedClient && (
                  <p><span className="font-medium">Client:</span> {selectedClient.company_name || selectedClient.name}</p>
                )}
                {selectedSite && (
                  <>
                    <p><span className="font-medium">Site:</span> {selectedSite.site_name}</p>
                    {selectedSite.location && (
                      <p><span className="font-medium">Location:</span> {selectedSite.location}</p>
                    )}
                  </>
                )}
              </div>
            </div>
          )}

          {/* Applicants List */}
          <div className="bg-muted rounded-lg p-4">
            <p className="text-sm font-medium text-foreground mb-3">
              Applicants to Convert ({applicants.length}):
            </p>
            <div className="space-y-2 max-h-48 overflow-y-auto">
              {applicants.map((applicant, index) => (
                <div key={applicant.id || index} className="bg-background rounded p-3 border border-border">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium text-foreground">
                        {applicant.applicant_name || applicant.name || `${applicant.first_name || ''} ${applicant.last_name || ''}`.trim()}
                      </p>
                      <p className="text-sm text-muted-foreground">{applicant.email}</p>
                      {applicant.position && (
                        <p className="text-xs text-muted-foreground mt-1">
                          Position: {applicant.position}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
            <div className="flex items-start gap-2">
              <Icon name="Info" size={16} className="text-blue-600 dark:text-blue-400 mt-0.5" />
              <div className="text-sm text-blue-800 dark:text-blue-300">
                <p className="font-medium mb-1">What happens when you convert:</p>
                <ul className="list-disc list-inside space-y-1 text-xs">
                  <li>Employee records will be created for each selected applicant</li>
                  <li>Employees will be assigned to the selected client and client site</li>
                  <li>Employee IDs will be auto-generated</li>
                  <li>Applicant data will be used to populate employee information</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center justify-end gap-3 pt-4 border-t border-border sticky bottom-0 bg-card">
            <Button variant="outline" onClick={onClose} disabled={loading}>
              Cancel
            </Button>
            <Button 
              onClick={handleConvert} 
              disabled={loading || !selectedClientId || !selectedSiteId}
              iconName="UserPlus"
              iconPosition="left"
            >
              {loading ? 'Converting...' : `Convert ${applicants.length} Applicant(s)`}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConvertToEmployeeModal;




